import clsx from 'clsx'
import {
  ChangeEvent,
  Dispatch,
  FocusEvent,
  FormEvent,
  forwardRef,
  HTMLProps,
  SetStateAction,
  useState,
} from 'react'
import Button from '../../atoms/button'
import CloseIcon from '../../atoms/icons/close-icon'
import FileIcon from '../../atoms/icons/file-icon'
import List from '../../atoms/list'
import ListItem from '../../atoms/list-item'

export interface FileInputProps
  extends Omit<HTMLProps<HTMLInputElement>, 'type'> {
  /**
   * Label of this input field.
   *
   * @default null
   */
  label?: string
  /**
   * Class name applied to input label.
   *
   * @default null
   */
  labelClassName?: string
  /**
   * Class name applied to input label and input field wrapper component.
   *
   * @default null
   */
  wrapperClassName?: string
  /**
   * Text which should appear below the input field.
   *
   * @default null
   */
  hint?: string
  /**
   * Determines whether hint text should be displayed in error color.
   *
   * @default false
   */
  error?: boolean
  /**
   * Text to be displayed when no files were selected by the user.
   *
   * @default 'No files are selected.'
   */
  placeholder?: string
  /**
   * Text to be displayed when files were already selected by the user.
   *
   * @default null
   */
  ctaPlaceholder?: string
  /**
   * Function called when one of the selected files are removed from the list.
   */
  onRemove?: (newFileList: FileList | Array<File>) => void
  /**
   * List of files displayed on the input field.
   *
   * @default null
   */
  files?: Array<File>
  onFileChange?: Dispatch<SetStateAction<Array<File>>>
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  function FileInput(
    {
      id,
      className,
      label,
      labelClassName,
      wrapperClassName,
      hint,
      error,
      onFocus,
      onBlur,
      onChange,
      onRemove,
      onReset,
      placeholder = 'Click here or drop your files here.',
      ctaPlaceholder,
      files: receivedFiles,
      onFileChange,
      ...props
    },
    ref,
  ) {
    const [focused, setFocused] = useState(false)
    const [localFiles, setFiles] = useState<Array<File>>([])

    const files = receivedFiles ?? localFiles

    function handleReset(event: FormEvent<HTMLInputElement>) {
      if (onReset) {
        onReset(event)
      }
    }

    function handleFocus(event: FocusEvent<HTMLInputElement>) {
      if (onFocus) {
        onFocus(event)
      }

      setFocused(true)
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
      if (onBlur) {
        onBlur(event)
      }

      setFocused(false)
    }

    function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
      if (onChange) {
        onChange(event)
      }

      // improvement proposal: store the union of the existing list and new
      // files in state
      if (event.target.files && event.target.files.length !== 0) {
        const fileList = Array.from(event.target.files)

        if (onFileChange) {
          onFileChange(fileList)
        } else {
          setFiles(fileList)
        }
      } else {
        appendFileListToInput(files)
      }
    }

    function handleRemoveFile(index: number) {
      const updateFileList = (currentFileList: Array<File>) => {
        const updatedFileList = currentFileList
          .slice(0, index)
          .concat(currentFileList.slice(index + 1))

        const fileListObject = appendFileListToInput(updatedFileList)

        if (onRemove) {
          onRemove(fileListObject || updatedFileList)
        }

        return updatedFileList
      }

      if (onFileChange) {
        onFileChange(updateFileList)
      } else {
        setFiles(updateFileList)
      }
    }

    function appendFileListToInput(inputFiles: Array<File>) {
      if (!id) {
        return
      }

      const fileInput = document.getElementById(id) as HTMLInputElement
      if (fileInput) {
        const fileList = new DataTransfer()

        for (const file of inputFiles) {
          fileList.items.add(file)
        }

        fileInput.files = fileList.files

        return fileList.files
      }

      return null
    }

    return (
      <div className={clsx('grid grid-flow-row gap-1', wrapperClassName)}>
        <label
          htmlFor={id}
          className={clsx(
            focused && 'text-primary font-semibold',
            !focused && error && 'text-red-500',
            !focused && !error && 'text-typography-hint',
            'transition-colors justify-self-start',
            labelClassName,
          )}
        >
          {label}
          {props.required ? <span>*</span> : null}
        </label>

        <div
          className={clsx(
            'bg-highlighted rounded-lg relative border-solid border-2 transition-colors grid items-center',
            files.length > 0 && 'py-4 px-4 md:px-6',
            files.length === 0 && 'h-24 p-6',
            focused && 'border-primary',
            !focused && error && 'border-red-500',
            className,
          )}
        >
          <input
            id={id}
            type="file"
            onChange={handleFileChange}
            ref={ref}
            className="absolute top-0 left-0 right-0 bottom-0 opacity-0 w-full h-full"
            onFocus={handleFocus}
            onBlur={handleBlur}
            onReset={handleReset}
            {...props}
          />

          {files.length === 0 && (
            <span className="text-typography-hint text-sm text-center">
              {placeholder}
            </span>
          )}

          {files.length > 0 && (
            <>
              <List className="z-50" density="dense">
                {files.map((file, index) => (
                  <ListItem key={file.name}>
                    <span className="grid grid-flow-col gap-2 place-content-start items-center break-all">
                      <FileIcon className="w-5 h-5" /> {file.name}
                    </span>

                    <Button
                      padding="same"
                      type="button"
                      variant="borderless"
                      className="w-11 h-11 justify-self-end"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <CloseIcon />
                    </Button>
                  </ListItem>
                ))}
              </List>

              {ctaPlaceholder && (
                <span className="text-typography-hint text-sm text-center mt-6">
                  {ctaPlaceholder}
                </span>
              )}
            </>
          )}
        </div>

        <span
          className={clsx(
            'min-h-5 text-sm',
            error ? 'text-red-500' : 'text-typography-hint',
          )}
        >
          {hint}
        </span>
      </div>
    )
  },
)

export default FileInput
